/**
 * Binary Apache Arrow live-map stream client (green-field, flag-gated).
 *
 * Connects to the server's columnar broadcaster (`/api/stream/ws/arrow`), subscribes to sources with a
 * scoped channel list, and decodes binary frames into columnar typed arrays that feed `liveColumnarStore`.
 * Control messages are JSON text frames; data messages are binary (8-byte header + Arrow IPC bytes).
 *
 * Decode keeps columns: `table.getChild(name).toArray()` yields the column's typed array — we NEVER call
 * `table.toArray()` (the row-materializing anti-pattern in `utils/global.ts parseBinary`).
 *
 * Isolation: separate socket from `streamingService` (the JSON path is untouched); feeds only the live-map
 * columnar store, nothing shared with explore/fleet.
 */

import * as arrow from 'apache-arrow';
import { apiEndpoints } from '@config/env';
import { debug, warn, error as logError } from '../utils/console';
import { liveColumnarStore, type ColumnarFrame } from '../store/liveColumnarStore';

const HEADER_BYTES = 8;
const MAGIC = 0x9e;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 60000;

/** LivePerformance tail notification (moved off the legacy JSON WS to the arrow control channel, Phase D3). */
export interface EventsAggregateReadyMessage {
  type: 'events_aggregate_ready';
  project_id: number;
  source_name?: string;
  event_type?: string;
  event_ids: number[];
}

function buildWsUrl(): string {
  const path = apiEndpoints.stream.websocketArrow;
  const proto = window.location.protocol.toLowerCase();
  const port = window.location.port;
  const hostname = window.location.hostname;
  const isSecure =
    proto === 'https:' ||
    port === '443' ||
    (!port && hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.'));
  const scheme = isSecure ? 'wss:' : 'ws:';
  let url = `${scheme}//${window.location.host}${path}`;
  if (isSecure && url.startsWith('ws://')) url = url.replace(/^ws:\/\//, 'wss://');
  return url;
}

class ArrowStreamClient {
  private ws: WebSocket | null = null;
  private connected = false;
  private stopped = true;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private sources: string[] = [];
  private channels: string[] = [];
  private consumers = new Map<string, { sources: string[]; channels: string[] }>();
  /** What the SERVER currently believes we are subscribed to, so departures can be told to it. */
  private subscribedSources = new Set<string>();
  private eventsHandlers = new Set<(m: EventsAggregateReadyMessage) => void>();
  private connectionHandlers = new Set<(connected: boolean) => void>();

  /** Start (or reconfigure) the stream for a set of source names + channel scope. Idempotent. */
  start(sourceNames: string[], channels: string[]): void {
    this.sources = sourceNames.map((s) => String(s).toUpperCase().trim());
    this.channels = channels;
    this.stopped = false;
    if (this.connected) {
      this.subscribeAll();
    } else if (!this.ws) {
      this.connect();
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(1000, 'client stop'); } catch { /* noop */ }
      this.ws = null;
    }
    this.connected = false;
    this.subscribedSources.clear();
  }

  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Ref-counted, union subscription so multiple components (e.g. the live map + a live timeseries) can share
   * the single socket without fighting over `start`/`stop` and the source/channel scope. Upserts this
   * consumer's scope (call again with the same `consumerId` to update its sources/channels), applies the union
   * of all consumers via `start`, and returns a release fn. The socket is only stopped when the LAST consumer
   * releases — so one component unmounting never tears down another's stream.
   */
  subscribe(consumerId: string, sourceNames: string[], channels: string[]): () => void {
    this.consumers.set(consumerId, { sources: sourceNames, channels });
    this.applyUnion();
    return () => this.release(consumerId);
  }

  /** Remove a consumer's subscription; re-applies the union, or stops the socket when none remain. */
  release(consumerId: string): void {
    if (!this.consumers.delete(consumerId)) return;
    if (this.consumers.size === 0) {
      this.stop();
      return;
    }
    this.applyUnion();
  }

  /** Compute the union of all consumers' sources + channels and (re)subscribe to it. */
  private applyUnion(): void {
    const sources = new Set<string>();
    const channels = new Set<string>();
    for (const c of this.consumers.values()) {
      for (const s of c.sources) sources.add(String(s).toUpperCase().trim());
      for (const ch of c.channels) channels.add(ch);
    }
    // An empty union with consumers still attached (e.g. every boat deselected) must QUIESCE the
    // stream, not leave the previous subscription running. Returning early here used to keep the
    // server pushing deltas for boats nobody was drawing.
    if (sources.size === 0) {
      this.sources = [];
      if (this.connected) this.subscribeAll();
      return;
    }
    this.start(Array.from(sources), Array.from(channels));
  }

  /** Open the socket (if not already) to receive control frames — for consumers that only need the events
   *  channel (LivePerformance), not a boat subscription. Does not change source subscriptions. */
  ensureConnected(): void {
    this.stopped = false;
    if (!this.connected && !this.ws) this.connect();
  }

  /** Subscribe to events_aggregate_ready control frames (LivePerformance tail); ensures the socket is open.
   *  Returns an unsubscribe fn. Does not close the socket on unsubscribe (the live map may share it). */
  onEventsAggregateReady(handler: (m: EventsAggregateReadyMessage) => void): () => void {
    this.eventsHandlers.add(handler);
    this.ensureConnected();
    return () => {
      this.eventsHandlers.delete(handler);
    };
  }

  /** Connection-status subscription (replaces streamingService.onConnection for the live-map/LivePerformance
   *  connection indicators). Fires true on open, false on close. */
  onConnection(handler: (connected: boolean) => void): () => void {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  private emitConnection(connected: boolean): void {
    for (const h of this.connectionHandlers) {
      try { h(connected); } catch { /* noop */ }
    }
  }

  private connect(): void {
    if (this.stopped) return;
    let url: string;
    try {
      url = buildWsUrl();
    } catch (err) {
      logError('[ArrowStreamClient] failed to build url', err);
      return;
    }
    debug('[ArrowStreamClient] connecting', url.replace(/token=[^&]+/, 'token=***'));
    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      logError('[ArrowStreamClient] WebSocket create failed', err);
      this.scheduleReconnect();
      return;
    }
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.connected = true;
      this.reconnectAttempts = 0;
      debug('[ArrowStreamClient] connected');
      this.emitConnection(true);
      this.subscribeAll();
    };
    this.ws.onmessage = (ev) => this.onMessage(ev);
    this.ws.onerror = () => warn('[ArrowStreamClient] socket error (details unavailable from browser)');
    this.ws.onclose = () => {
      this.connected = false;
      this.ws = null;
      // The server forgot us, so the next connection must re-subscribe everything from scratch.
      this.subscribedSources.clear();
      this.emitConnection(false);
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer != null) return;
    this.reconnectAttempts++;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (this.reconnectAttempts - 1));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(obj)); } catch { /* noop */ }
    }
  }

  /**
   * Reconcile the server's view with `this.sources`: unsubscribe what left, then (re)subscribe the rest.
   *
   * Every current source is re-sent even when unchanged — channel scope is per-client and
   * last-subscribe-wins server-side, so a widened channel list only takes effect by re-subscribing.
   * That also re-sends a snapshot per source, which is harmless: `liveColumnarStore.appendFrame`
   * drops non-newer timestamps, as does the live timeseries buffer.
   */
  private subscribeAll(): void {
    const next = new Set(this.sources);
    for (const name of this.subscribedSources) {
      if (!next.has(name)) {
        this.send({ type: 'unsubscribe', payload: { source_name: name } });
      }
    }
    for (const name of next) {
      this.send({ type: 'subscribe', payload: { source_name: name, channels: this.channels } });
    }
    this.subscribedSources = next;
  }

  private onMessage(ev: MessageEvent): void {
    if (typeof ev.data === 'string') {
      this.onControl(ev.data);
      return;
    }
    if (ev.data instanceof ArrayBuffer) {
      this.onBinary(ev.data);
    }
  }

  private onControl(text: string): void {
    let msg: { type?: string; map?: Record<string, string>; [k: string]: unknown };
    try {
      msg = JSON.parse(text);
    } catch {
      return;
    }
    if (msg.type === 'stream_reset') {
      // Session boundary (simulation started/stopped): everything retained belongs to the previous session.
      // Sim samples carry wall-clock timestamps, so they are indistinguishable from live data by age — the
      // server telling us is the only signal that they are stale. A fresh source_registry + snapshot follows.
      debug(`[ArrowStreamClient] stream reset (${String(msg.reason ?? 'unspecified')}) — dropping retained data`);
      liveColumnarStore.clearAll();
      return;
    }
    if (msg.type === 'source_registry' && msg.map) {
      liveColumnarStore.setSourceRegistry(msg.map);
      return;
    }
    if (msg.type === 'events_aggregate_ready') {
      for (const h of this.eventsHandlers) {
        try { h(msg as unknown as EventsAggregateReadyMessage); } catch { /* noop */ }
      }
      return;
    }
    // 'connected' / 'subscribed' / 'pong' / 'error' need no client action here.
  }

  private onBinary(buf: ArrayBuffer): void {
    if (buf.byteLength <= HEADER_BYTES) return;
    const header = new DataView(buf, 0, HEADER_BYTES);
    if (header.getUint8(0) !== MAGIC) return;
    const reset = (header.getUint8(3) & 0x01) === 1;
    try {
      const table = arrow.tableFromIPC(new Uint8Array(buf, HEADER_BYTES));
      const frame = this.tableToFrame(table, reset);
      if (frame) liveColumnarStore.appendFrame(frame);
    } catch (err) {
      warn('[ArrowStreamClient] failed to decode frame', err);
    }
  }

  /** Extract columns as typed arrays (no row materialization). */
  private tableToFrame(table: arrow.Table, reset: boolean): ColumnarFrame | null {
    const sidVec = table.getChild('source_id');
    const tsVec = table.getChild('ts');
    if (!sidVec || !tsVec) return null;
    const sourceIds = sidVec.toArray() as Int16Array;
    const ts = tsVec.toArray() as Float64Array;
    const channels: Record<string, ArrayLike<number>> = {};
    for (const field of table.schema.fields) {
      const name = field.name;
      if (name === 'source_id' || name === 'ts' || name === 'hist_ts') continue;
      const col = table.getChild(name);
      if (col) channels[name] = col.toArray() as ArrayLike<number>;
    }
    return { sourceIds, ts, channels, reset };
  }
}

export const arrowStreamClient = new ArrowStreamClient();
